import * as Enumerable from "linq";
import { bindable } from 'aurelia-templating';
import { computedFrom } from 'aurelia-binding';
import { autoinject } from 'aurelia-dependency-injection';
import { FileHelper } from '../../../../helpers/file-helper';
import { BindingSignaler } from 'aurelia-templating-resources';
import { Reflection } from '../../../../utilities/reflection';
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { LookupValue } from '../../../../models/lookups/lookup.models';
import { AttachmentHelper } from '../../../../helpers/attachment-helper';
import { AttachmentsHelper } from '../../../../utilities/attachments-helper';
import { type WorkflowStateModel } from '../../../../models/workflow/workflow.models';
import { EmailRecipientTypeEnum } from '../../../../models/vetting/vetting.models';
import { DialogServiceConstants } from '../../../../constants/basics/dialog.constants';
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityAttachmentModel } from '../../../../models/attachment/attachment.models';
import { AttachmentClientService } from '../../../../services/attachment/attachments-client.service';
import { RightShipOnboardingQuestionCommentService } from "../../../../services/rightship-onboarding/rightship-onboarding-question-comment.service";

@autoinject()
export class NeedMoreInfoView {
    
    @bindable 
    public needMoreInfoType: string;

    @bindable 
    public vetId: number;

    @bindable
    public recipients: any[] = [];

    @bindable
    public workflowState : WorkflowStateModel;

    @bindable 
    public isHQ: boolean;

    @bindable 
    public isHayPoint: boolean;

    @bindable
    public acceptableDayOnlyReasons: LookupValue[] = [];

    @bindable
    public acceptableDayOnlyReason: number;

    @bindable 
    public message: string = "";

    @bindable
    public files: File[] = [];

    @bindable
    public showVettingContactView: boolean;

    @bindable
    public disableAutoComplete: boolean;

    @bindable 
    public vettingContactEmails: { email: string, emailRecipientTypeId: number, isShipManager: boolean }[];

    @bindable
    public vettingContactMessage: string;

    @bindable
    public previousAttachments: EntityAttachmentModel[] = null;
    
    @bindable
    public correspondenceEmails: any[];

    private isRecipientsBound = false;
    private isCorrespondenceBound = false;

    constructor(
        private attachmnetService: AttachmentClientService,
        private dialogClientService: DialogClientService,
        private bindingSignaler: BindingSignaler,
        public questionCommentsService: RightShipOnboardingQuestionCommentService)
    {}

    setRecipientsType() {
        
        this.recipients.forEach(x => {
            const emailMatches = this.correspondenceEmails?.filter(y => y.email == x.recipientEmail) ?? [];
            //case when both ship manager and requestor email are the same
            if(emailMatches.length > 1) {
                x.emailRecipientType = emailMatches.filter(x => x.isShipManager).length > 0?  EmailRecipientTypeEnum.To : EmailRecipientTypeEnum.Bcc;
            }
            else {
                x.emailRecipientType = emailMatches[0]?.isShipManager? EmailRecipientTypeEnum.To : EmailRecipientTypeEnum.Bcc;
            }
            
        });
    }   

    recipientsChanged() { 
        this.isRecipientsBound = true;

        if (this.isCorrespondenceBound) {
            this.setRecipientsType();
        }
    }     

    correspondenceEmailsChanged() {
        this.isCorrespondenceBound = true;
        if (this.isRecipientsBound) {
            this.setRecipientsType();
            this.bindingSignaler.signal("recipient-changed")
        }
    }
    
    recipientEmailChanged() {
        this.bindingSignaler.signal("recipient-changed")
    }

    public getEmailLabel(email) {
        const emailMatches = this.correspondenceEmails?.filter(x => x.email == email) ?? [];
        //case when both ship manager and requestor email are the same
        if(emailMatches.length > 1) {
           return emailMatches.filter(x => x.isShipManager).length > 0? "(Ship Manager)" : emailMatches[0]?.recipientType
        }
        else {
           return emailMatches[0]?.recipientType
        }
    }

    @computedFrom(Reflection.getPropertyPath<NeedMoreInfoView>(z => z.recipients), 
        Reflection.getPropertyPath<NeedMoreInfoView>(z => z.recipients.length))
    get orderedRecipients() {        
        //sorts to put "To" emails at the top. TO has priority over CC and BCC due to the enum number. 
        //TO is 1, CC is 2 and BCC is 3
        //so when sorting, emails will be in that order 
        return Enumerable.from(this.recipients).orderBy(x => x.emailRecipientType).toArray();
    }

    addEmail() {
        if (!this.recipients) {
            this.recipients = [];
        } 
        let recipientEntityId = 0;
        let recipientEntityTypeId = 0;

        if(this.recipients[0]) {
            recipientEntityId =  this.recipients[0].recipientEntityId;
            recipientEntityTypeId = this.recipients[0].recipientEntityTypeId;
        }
        else
        {
            recipientEntityId =  this.workflowState.entityId;
            recipientEntityTypeId = EntityTypes.VesselApplicationForms;
        }

        this.recipients.push({
            recipientEmail: '',
            recipientEntityId: recipientEntityId,
            recipientEntityTypeId: recipientEntityTypeId,
            emailRecipientType: EmailRecipientTypeEnum.Bcc
        });
    }

    removeEmail(recipient) {

        const index = this.recipients.findIndex(x => x == recipient);

        this.recipients.splice(index, 1);
        if(this.recipients.length == 1) {
            this.recipients[0].emailRecipientType = EmailRecipientTypeEnum.To as number
        }
    }

    removeAttachment(index) {
        this.previousAttachments.splice(index, 1);
    }

    openFile(file) {
        if (file && file.path) {
            let path = AttachmentsHelper.getUrl(file.path);
            const downloadFile = !(this.isPDF(path) || this.isImage(path));
            this.attachmnetService.getSharedAccessSignatureToken(file.entityAttachmentId, downloadFile).then(sasToken => {
                if (sasToken) {
                    path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                }

                if (!downloadFile) {
                    this.dialogClientService.show(DialogServiceConstants.URLs.ObjectViewer,
                        {
                            link: path,
                            doNotAddExtension: true
                        }, false, false, true);
                }
                else {
                    FileHelper.downloadFile(AttachmentsHelper.getUrl(path), file.documentName);
                }
            });
        }
    }
    public isPDF(link): boolean {
        return link.indexOf('.pdf') != -1;
    }

    public isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg"];
        return imageExtensions.some(imageExtension => link.indexOf(imageExtension) != -1);
    }

    getSize(attachment) {
        if(attachment.fileSize) return AttachmentHelper.formatBytes(attachment.fileSize)
        return ""
    }
}